import React, { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Fade,
  Zoom
} from '@mui/material';
import {
  Build,
  Settings,
  AttachMoney,
  Assignment,
  Check,
} from '@mui/icons-material';
import LoadingImage from '../components/LoadingImage';

const Services: React.FC = () => {
  // SEO Meta Tags
  useEffect(() => {
    document.title = "Hizmetlerimiz - Kale Treyler Frigo | Araç İçi Yalıtım, Double Deck, Yedek Parça";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Kale Treyler Frigo hizmetleri: Araç içi yalıtım, double deck çift katlı sistem, yedek parça ve genel tamir hizmetleri. Mersin'de profesyonel frigo dorse tamiri.");
    }
    
    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', "frigo dorse hizmetleri, araç içi yalıtım, double deck sistem, yedek parça, genel tamir, Mersin frigo servis");
    }
    
    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', "Hizmetlerimiz - Kale Treyler Frigo | Araç İçi Yalıtım, Double Deck, Yedek Parça");
    }
    
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', "Kale Treyler Frigo hizmetleri: Araç içi yalıtım, double deck çift katlı sistem, yedek parça ve genel tamir hizmetleri. Mersin'de profesyonel frigo dorse tamiri.");
    }

    // Update Canonical URL
    const canonicalLink = (document.querySelector('link[rel="canonical"]') || document.createElement('link')) as HTMLLinkElement;
    canonicalLink.setAttribute('rel', 'canonical');
    canonicalLink.setAttribute('href', 'https://kaletreyler.com/hizmetler');
    if (!document.head.contains(canonicalLink)) {
      document.head.appendChild(canonicalLink);
    }
  }, []);

  const services = [
    {
      title: "Genel Tamir Hizmetleri",
      description: "Frigo dorse kasalarında kapsamlı tamir ve bakım hizmetleri sunuyoruz.",
      icon: <Assignment sx={{ fontSize: 60, color: 'primary.main' }} />,
      features: ["Frigo kasası tamiri", "İzolasyon tamiri", "Kapı ve menteşe tamiri","Alt Takım tamiri","Montaj ve Demontaj Hizmeti"],
      image: "/18.jpeg"
    },
    {
      title: "Araç İçi Yalıtım",
      description: "Frigo dorse kasalarında profesyonel yalıtım çözümleri ile enerji tasarrufu sağlıyoruz.",
      icon: <Build sx={{ fontSize: 60, color: 'primary.main' }} />,
      features: ["Poliüretan yalıtım", "Sızdırmazlık Uygulamaları", "Kapı contası değişimi", "Enerji verimliliği artışı"],
      link: "/hizmetler/arac-ici-yalitim",
      image: "/22.png"
    },
    {
      title: "Double Deck Çift Katlı Sistem",
      description: "Çift katlı frigo dorse sistemlerinde uzman tamir ve bakım hizmeti sunuyoruz.",
      icon: <Settings sx={{ fontSize: 60, color: 'primary.main' }} />,
      features: ["Çift katlı sistem tamiri", "Çift katlı sistem montajı ve demontajı", "Çift katlı sistem satışı", "Rafların tamiri ve bakımı"],
      link: "/hizmetler/double-deck",
      image: "/24.jpg"
    },
    {
      title: "Tır - Dorse Yedek Parça ve Aksesuarları -  Hırdavat ve Cıvata",
      description: "Orijinal ve kaliteli yedek parçalarla frigo dorse kasalarınızı yeniden hayata döndürüyoruz.",
      icon: <AttachMoney sx={{ fontSize: 60, color: 'primary.main' }} />,
      features: ["Orijinal yedek parçalar", "Hızlı teslimat", "Garantili parçalar", "Uygun fiyat garantisi"],
      link: "/hizmetler/yedek-parca",
      image: "/23.png"
    }
  ];

  const steps = [
    'Arıza Tespiti',
    'Teknik Analiz',
    'Tamir Planı',
    'Tamir ve Test'
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Paper
        elevation={0}
        sx={{
          background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
          color: 'white',
          py: 8,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md" sx={{ px: { xs: 2, md: 4, lg: 6 } }}>
          <Fade in timeout={1000}>
            <Box>
              <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Hizmetlerimiz
              </Typography>
              <Typography variant="h5" paragraph>
                Kale Treyler olarak frigo dorse tamirinde sunduğumuz kapsamlı servis hizmetleri
              </Typography>
            </Box>
          </Fade>
        </Container>
      </Paper>

      {/* Services Grid */}
      <Container maxWidth="xl" sx={{ py: 8, px: { xs: 2, md: 4, lg: 6 } }}>
        <Grid container spacing={{ xs: 4, md: 6, lg: 8 }}>
          {services.map((service, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Zoom in timeout={1000 + index * 200}>
                <Card
                  elevation={3}
                  sx={{
                    height: '100%',
                    transition: 'all 0.3s ease',
                    borderRadius: 3,
                    '&:hover': {
                      transform: 'translateY(-10px)',
                      boxShadow: '0 20px 40px rgba(30, 58, 138, 0.2)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box 
                      mb={2}
                      sx={{
                        display: 'inline-flex',
                        p: 2,
                        borderRadius: '50%',
                        bgcolor: 'primary.50',
                        border: '2px solid rgba(30, 58, 138, 0.2)',
                        width: 120,
                        height: 120,
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden'
                      }}
                    >
                      {service.image ? (
                        <LoadingImage
                          src={service.image}
                          alt={service.title}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: '50%',
                          }}
                        />
                      ) : (
                        service.icon
                      )}
                    </Box>
                    <Typography variant="h4" component="h3" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main', mb: 2 }}>
                      {service.title}
                    </Typography>
                    <Typography variant="body1" paragraph color="text.secondary" sx={{ lineHeight: 1.5, mb: 2 }}>
                      {service.description}
                    </Typography>
                    <List sx={{ py: 0 }}>
                      {service.features.map((feature, featureIndex) => (
                        <ListItem key={featureIndex} sx={{ pl: 0, py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <Check sx={{ color: 'primary.main', fontSize: 20 }} />
                          </ListItemIcon>
                          <ListItemText 
                            primary={feature}
                            sx={{ color: 'text.primary' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                    {service.link && (
                      <Box sx={{ mt: 2, textAlign: 'center' }}>
                        <Button
                          component={RouterLink}
                          to={service.link}
                          variant="contained"
                          size="large"
                          sx={{
                            borderRadius: 2,
                            px: 4,
                            py: 1.5,
                            textTransform: 'none',
                            fontWeight: 'bold',
                            bgcolor: 'primary.main',
                            '&:hover': {
                              bgcolor: 'primary.dark',
                              transform: 'translateY(-2px)',
                            },
                            transition: 'all 0.2s ease',
                          }}
                        >
                          Detayları Görüntüle
                        </Button>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Zoom>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Process Section */}
      <Box sx={{ bgcolor: 'primary.50', py: 8 }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, md: 4, lg: 6 } }}>
          <Box textAlign="center" mb={6}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
              Tamir Sürecimiz
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Frigo dorse tamirini nasıl gerçekleştirdiğimizi keşfedin
            </Typography>
          </Box>

          <Stepper 
            activeStep={-1} 
            alternativeLabel
            sx={{
              '& .MuiStepLabel-root .Mui-completed': {
                color: 'primary.main',
              },
              '& .MuiStepLabel-root .Mui-active': {
                color: 'primary.main',
              },
              '& .MuiStepLabel-root .MuiStepLabel-iconContainer': {
                color: 'primary.main',
              },
            }}
          >
            {steps.map((label, index) => (
              <Step key={label}>
                <StepLabel>
                  <Typography variant="h6">{label}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {index === 0 && 'Frigo dorse kasasındaki arızayı detaylı olarak tespit ediyoruz'}
                    {index === 1 && 'Teknik analiz yaparak tamir planını hazırlıyoruz'}
                    {index === 2 && 'Uzman teknisyenlerimizle tamir planını uyguluyoruz'}
                    {index === 3 && 'Tamir sonrası test ve kalite kontrolü yapıyoruz'}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Container>
      </Box>

    </Box>
  );
};

export default Services; 